import { datadogRum } from '@datadog/browser-rum';
import { env } from '@repo/env';
import type { SegmentAbstraction } from '@repo/segment/Analytics/lib/Segment';
import type { ExperimentConfig } from '../experiments/types';
import type { ABSmartlyEventLogger } from '../types';
import { logABSmartlyBucketingDecision } from './logBucketingDecision';
import { isStaticExperiment } from './server';
import type { SupportedExperimentVariants } from './types';
import { getVariantsArray } from './variants';

export type ExposureData = {
  experimentKey: string;
  variationKey: string;
  unitType: string | null;
};

type Props = {
  analytics: SegmentAbstraction;
  anonymousId: string | null;
  deviceId: string;
  deployableVersion: string;
  gitSha: string;
  staticExperimentResult: SupportedExperimentVariants;
  experiments: ExperimentConfig[];
};

export function retrieveExternalDomainURL(url: string) {
  const oldUrl = new URL(url);
  let host = 'https://localnerd.stage.nerdwallet.biz';

  if (env.NEXT_PUBLIC_DEPLOYABLE_ENV === 'prod') {
    host = 'https://www.nerdwallet.com';
  } else if (env.NEXT_PUBLIC_DEPLOYABLE_ENV === 'stage') {
    host = 'https://stage.nerdwallet.biz';
  }

  return new URL(oldUrl.toString().replace(`${oldUrl.protocol}//${oldUrl.host}`, host));
}

export const getABSmartlyEventLogger = ({
  analytics,
  anonymousId,
  deviceId,
  deployableVersion,
  gitSha,
  staticExperimentResult,
  experiments,
}: Props) => {
  const absmartlyEventLogger: ABSmartlyEventLogger = (context, eventName, data = {}) => {
    if (eventName === 'error') {
      let errorData = data;

      if (!(errorData instanceof Error)) {
        errorData = new Error(`Unknown ABSmartly Error ${JSON.stringify(errorData)}`);
      }

      datadogRum.addError(errorData, {
        eventName,
        deployableVersion,
        source: 'ABSmartly',
        message: 'ABSmartly error',
        rawData: errorData,
      });
    }

    if (eventName === 'publish' && 'exposures' in data) {
      const exposures = data.exposures ?? [];
      const exposureEvents = exposures.reduce((acc: ExposureData[], exposure) => {
        const { name: experimentId, variant: variantIndex } = exposure;
        const defaultEmptyValue = '';
        const variants = getVariantsArray(context, experimentId, defaultEmptyValue);
        const isStatic = isStaticExperiment(experiments, experimentId);
        const variationKey = isStatic ? staticExperimentResult : variants[variantIndex];
        const shouldSkipExposure = variationKey === defaultEmptyValue || exposure.audienceMismatch;

        if (shouldSkipExposure) {
          return [...acc];
        } else {
          return [
            ...acc,
            {
              variationKey,
              experimentKey: experimentId,
              unitType: exposure.unit,
            },
          ];
        }
      }, []);

      const href = window.location?.href;

      // Log the exposure data to Segment
      logABSmartlyBucketingDecision(
        {
          deployableVersion,
          anonymousId,
          deviceId,
          attributes: {
            page_url: href ? retrieveExternalDomainURL(href).toString() : '',
          },
          exposureData: exposureEvents,
          gitSha,
        },
        analytics,
      );
    }
  };

  return absmartlyEventLogger;
};
